import React, { useState, useRef, useCallback } from "react";
import get from "lodash/get";
import differenceWith from "lodash/differenceWith";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera } from "@fortawesome/free-solid-svg-icons/faCamera";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons/faChevronRight";
import {
  getFallbackImageFromUrl,
  getImageFromUrl,
  getTouchPointThumb,
  getTouchPointThumbFallback,
} from "../../utils/imageService";
import { FixedSizeList as List } from "react-window";
import touchpointVideoIcon from "../../static/images/touchpointVideoIcon.svg";
import SurveyBigIcon from "../../static/images/SurveyBigIcon.svg";
import { TP_LIST_ITEM_WIDTH, TP_LIST_ITEM_HEIGHT } from "../../utils/constants";
import { showOverlay } from "../../utils/touchpoint";
import { BrandificationImageFallback } from "../../components";
import "./styles.scss";

const TouchPointList = ({ viewProfile, goToDetails, list = [], role }) => {
  const listRef = useRef(null);
  const [parentWidth, serParentWidth] = useState(null);
  const [nextVisible, setNextVisible] = useState(true);
  const [prevVisible, setPrevVisible] = useState(false);
  const [parentEl, setParentEl] = useState(null);

  const setParent = el => {
    setParentEl(el);
    serParentWidth(get(el, "offsetWidth", null));
  };

  const goToProfile = (user, e) => {
    if (viewProfile) {
      viewProfile(user, e);
    }
  };

  const handleMediaPrev = () => {
    let currentOffset = listRef.current.state.scrollOffset;
    let offsetToMove = currentOffset - TP_LIST_ITEM_WIDTH >= 0 ? TP_LIST_ITEM_WIDTH : currentOffset;
    currentOffset -= offsetToMove;
    listRef.current.scrollTo(currentOffset);
    handleScroll();
  };

  const handleMediaNext = () => {
    let currentOffset = listRef.current.state.scrollOffset;
    let maxScrollOffset = listRef.current.props.maxScrollOffset;
    let offsetToMove =
      currentOffset + TP_LIST_ITEM_WIDTH <= maxScrollOffset ? TP_LIST_ITEM_WIDTH : maxScrollOffset - currentOffset;
    currentOffset += offsetToMove;
    listRef.current.scrollTo(currentOffset);
    handleScroll();
  };

  const handleScroll = () => {
    let currentOffset = listRef.current && listRef.current.state.scrollOffset;
    const width = get(parentEl, "current.offsetWidth", null);
    const maxScrollOffset = list.length * TP_LIST_ITEM_WIDTH - (parentWidth || width);

    if (currentOffset === 0) {
      setPrevVisible(false);
    }
    if (currentOffset === maxScrollOffset) {
      setNextVisible(false);
    }
    if (currentOffset > 0 && !prevVisible) {
      setPrevVisible(true);
    }
    if (currentOffset < maxScrollOffset && !nextVisible) {
      setNextVisible(true);
    }
  };

  const renderWidget = useCallback(
    (touchpoint, index, role) => {
      return (
        <div className="touchpoint" key={index} onClick={() => goToDetails(touchpoint._id)}>
          <BrandificationImageFallback
            src={getTouchPointThumb(touchpoint)}
            fallbackImage={getTouchPointThumbFallback(touchpoint)}
            className={touchpoint.mediaURLs && touchpoint.mediaURLs[0] ? "touchpoint-image-container" : "no-image"}
          />
          {touchpoint.mediaURLs && touchpoint.mediaURLs[0] && touchpoint.mediaURLs[0].video && (
            <img src={touchpointVideoIcon} className="touchpoint-video-icon" alt="" />
          )}
          {touchpoint.surveyState && showOverlay(touchpoint, role) && (
            <>
              <img src={SurveyBigIcon} className="touchpoint-survey-icon" alt="" />
            </>
          )}
          {touchpoint.mediaURLs.length > 1 && !showOverlay(touchpoint, role) && (
            <div className="touchpoint-camera">
              <FontAwesomeIcon icon={faCamera} />
              <div className="touchpoint-media-count">{touchpoint.mediaURLs.length}</div>
            </div>
          )}
          <div className="touchpoint-category">{touchpoint.category && touchpoint.category.name}</div>
          <div
            className={
              touchpoint.category
                ? "touchpoint-category second-category second-item"
                : "touchpoint-category second-category"
            }
          >
            {touchpoint.category2 && touchpoint.category2.name}
          </div>
          <div
            className={
              touchpoint.category && touchpoint.category2
                ? "touchpoint-category third-category third-item"
                : !touchpoint.category && !touchpoint.category2
                  ? "touchpoint-category third-category"
                  : "touchpoint-category third-category second-item"
            }
          >
            {touchpoint.category3 && touchpoint.category3.name}
          </div>
          <div className="touchpoint-name">{touchpoint.name}</div>
          {touchpoint.user && (
            <div className="touchpoint-user-info" onClick={e => goToProfile(touchpoint.user, e)}>
              <BrandificationImageFallback
                src={getImageFromUrl(touchpoint.user.photoURL)}
                fallbackImage={getFallbackImageFromUrl(touchpoint.user.photoURL)}
                className={touchpoint.user.photoURL ? "touchpoint-user-logo-div" : "touchpoint-user-logo-div no-image"}
              />
            </div>
          )}
        </div>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list],
  );

  const width = get(parentEl, "current.offsetWidth", null);
  const maxScrollOffset = list.length * TP_LIST_ITEM_WIDTH - (parentWidth || width);
  const maxItemsToDisplay = (parentWidth || width) / TP_LIST_ITEM_WIDTH;
  return (
    <div ref={setParent} className="tp-list-wrapper">
      {list.length > maxItemsToDisplay && prevVisible && (
        <div className="btn-slider btn-prev-media" onClick={handleMediaPrev}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </div>
      )}
      {parentWidth && (
        <List
          ref={listRef}
          onScroll={handleScroll}
          className="tp-list"
          direction="horizontal"
          height={TP_LIST_ITEM_HEIGHT}
          itemCount={list?.length}
          itemSize={TP_LIST_ITEM_WIDTH}
          width={parentWidth || width}
          maxScrollOffset={maxScrollOffset}
        >
          {({ key, index, style }) => (
            <div className={index % 2 ? "ListItemOdd" : "ListItemEven"} key={key} style={style}>
              {renderWidget(list[index], index, role)}
            </div>
          )}
        </List>
      )}
      {list.length > maxItemsToDisplay && nextVisible && (
        <div className="btn-slider btn-next-media" onClick={handleMediaNext}>
          <FontAwesomeIcon icon={faChevronRight} />
        </div>
      )}
    </div>
  );
};

function propsAreEqual(prev, next) {
  if (prev.list?.length !== next.list?.length) {
    return false;
  }
  return differenceWith(prev.list, next.list, (a, b) => a._id === b._id)?.length === 0;
}

export default React.memo(TouchPointList, propsAreEqual);
